/**
 * Labelled axis triad + north indicator for the verification workspace.
 *
 * Anchored at the ORIGINAL support crop box (`GET_VERIFIED_CROP_BOX`), never the
 * live measurement box. The triad is the fixed spatial reference a surveyor
 * reads a deviation direction against — "+4 mm" only means something once you
 * know which way +X points — so it must stay put while slicing narrows the crop.
 *
 * Axes carry their SURVEY names, not just letters, because the letters are not
 * the vocabulary the report speaks: the per-point tables label their rows
 * Eastings / Northings / Elevation. A surveyor reading "-2 mm Northings" on the
 * PDF should be able to find that direction in the viewer without translating
 * through an axis letter.
 *
 * NORTH = +Y IS THE DEMO CONTRACT. It follows from that same report mapping —
 * Eastings / Northings / Elevation are world X / Y / Z — so grid north is +Y by
 * construction, not by measurement. A real site has a project-north bearing that
 * differs from grid north, and a survey that is not axis-aligned would need that
 * bearing as an input here. Treat the north arrow as correct for the demo model
 * and as needing a bearing before it is trusted on a live site.
 *
 * Display-only: nothing here is registered for raycast or hit-testing. The spike
 * found that invisible 3D handles silently hijack viewer navigation (see
 * `.claude/features/AVA-475_SPIKE_FINDINGS.md`), and an orientation reference
 * has no reason to be clickable. That is the one way this deliberately differs
 * from the crop box's control arrows, which it otherwise mirrors.
 *
 * ── WHAT THIS BUNDLE ACTUALLY RENDERS ───────────────────────────────────────
 * Three constructions were tried; only the third survived a look at the real
 * app. All three are recorded because the two failures are NOT deducible from
 * the viewer source, and the next person will otherwise re-derive them:
 *
 *   1. `THREE.Sprite` + `SpriteMaterial` labels — DEAD. No pixels at all in an
 *      overlay scene, despite the bundle containing a `SpritePlugin` that
 *      honours `depthTest`/`depthWrite` and despite overlay scenes reaching
 *      `WebGLRenderer.render()`. Static bundle inspection predicted this would
 *      work; the live app disagreed. Labels are `PlaneGeometry` +
 *      `MeshBasicMaterial` billboards instead — the CML marker pattern
 *      (`forgeService.js:4280`), which is on screen in this app today.
 *   2. `ArrowHelper`'s stock head via the constructor's `headLength`/`headWidth`
 *      — DEAD. Renders nothing visible. That is precisely why the in-repo
 *      precedent `threejsUtils.js:291` replaces `cone.geometry` outright, and
 *      why this module now does the same.
 *   3. Bare `ArrowHelper` lines — drawn, but r71 `linewidth` is ignored on
 *      Windows/ANGLE, so they are 1 px hairlines that read as broken at demo
 *      resolution. Each arrow therefore gets a cylinder shaft over its line,
 *      exactly as the crop box's control arrows do.
 *
 * WHY DEPTH TESTING IS OFF ON EVERY MATERIAL — the triad is anchored at
 * `box.min`, a corner that sits inside the support geometry, so a depth-tested
 * triad is simply invisible. The in-repo arrow and label precedents
 * (`threejsUtils.js:290`, `forgeService.js:4280`) disable both flags for the
 * same reason.
 *
 * ⚠ DISPOSAL — ownership is mixed, so read this before adding a `dispose()`:
 *   • cone geometry ......... OURS. We replace r71's with a sized head. Dispose.
 *   • shaft cylinder ........ OURS (geometry + material), and a plain Mesh, so
 *                             `removeFromScene` releases both unaided.
 *   • label plane + material  OURS via `removeFromScene`. Its canvas TEXTURE is
 *                             NOT reached by that helper and is disposed here.
 *   • line / cone materials . OURS (per-instance), but an ArrowHelper is a bare
 *                             Object3D so `removeFromScene` cannot reach them.
 *   • line geometry ......... NOT OURS. `THREE.ArrowHelper` in this r71 build
 *                             takes its line geometry from an object built ONCE
 *                             inside the constructor's factory closure and
 *                             shared by every ArrowHelper in the application,
 *                             the crop box's control arrows included. Disposing
 *                             it would blank those. Leave it alone.
 */
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';
import { addToScene, removeFromScene } from '@/utils/forgeUtils';
import { drawCylinder } from '@/utils/threejsUtils';

/**
 * Our own overlay scene. The crop box owns `potree-scene`; removing our meshes
 * out of a scene an extension is also writing to invites taking its chrome down
 * with ours. Feature-scoped scene names are the established convention here
 * (`cml-markers-scene`, `tml-markers-scene`).
 */
const SCENE_NAME = 'verification-axis-triad';

/**
 * Marks the billboard label meshes. Load-bearing, not decoration: the opacity
 * walk uses it to tell a label (always transparent, for its canvas alpha) from
 * a solid part.
 */
const LABEL_NAME = 'axis-triad-label';

/**
 * Arrow length as a fraction of the crop box's longest edge, then clamped.
 *
 * A typical support's crop box measures ~1.66 m across, giving ~207 mm arrows:
 * long enough to read an axis direction at a glance, short enough not to pull
 * the eye off the support. The floor keeps a small bracket's triad legible; the
 * ceiling stops a pathological crop — dbIds resolving to a whole floor — from
 * drawing arrows across the scene.
 *
 * The ratio and both bounds were halved together so the rebase is proportional
 * across every support size, not tuned to the one we looked at.
 *
 * THE RATIO IS UNITLESS AND SURVIVES ANY FACILITY; THE TWO BOUNDS ARE REAL
 * METRES AND DO NOT. Viewer world units are metres on some models and
 * millimetres on others (see utils/modelUnits.js), so both bounds are converted
 * to viewer units before the clamp. Applied raw on a millimetre model the
 * ceiling is 0.75 of a MILLIMETRE, and the triad collapses to three invisible
 * stubs at the box corner — which is what it did on Merchant.
 */
// User-calibrated 2026-07-31: the previous 0.25 base read double-size at 1×.
const AXIS_LENGTH_RATIO = 0.125;
const AXIS_LENGTH_MIN_M = 0.075;
const AXIS_LENGTH_MAX_M = 0.75;

/**
 * Head size as a fraction of arrow length — LENGTH along the axis, WIDTH across
 * it (a diameter, not a radius). Generous on purpose: a head sized for print
 * proportions vanishes at demo resolution, which is how the first pass ended up
 * showing four headless hairlines.
 */
const HEAD_LENGTH_RATIO = 0.18;
const HEAD_WIDTH_RATIO = 0.08;

/**
 * Shaft radius as a fraction of arrow length. The shaft exists only because r71
 * `linewidth` is ignored on Windows/ANGLE — see the header. Kept thinner than
 * the head's base radius (0.04 × length) so the arrow still reads as an arrow.
 */
const SHAFT_RADIUS_RATIO = 0.015;

/**
 * Label HEIGHT as a fraction of its own arrow's length: 0.3 gives a ~62 mm tall
 * label beside a ~207 mm arrow on a typical support. Height, not width — width
 * follows from the canvas aspect, so a long survey name grows sideways instead
 * of shrinking its own text.
 */
const LABEL_SCALE_RATIO = 0.3;
/**
 * Clear air between the cone tip and the label's LEADING edge, as a fraction of
 * label height. Label meshes are centred on their position, so a label is pushed
 * out by this gap plus half its own extent along the axis — without that
 * half-extent term a long name would straddle the tip and half of it would run
 * back through the support.
 */
const LABEL_TIP_GAP_RATIO = 0.15;

/**
 * Label canvases are power-of-two in both dimensions, so NPOT texture filtering
 * never comes into it. Height is shared; width is per-variant — wide for the
 * survey names, square for a single glyph like "N".
 */
const LABEL_CANVAS_H_PX = 128;
const AXIS_LABEL_CANVAS_W_PX = 512;
const GLYPH_CANVAS_W_PX = 128;

/**
 * Base font, then shrink-to-fit. "Y · Northings" overruns 512 px at the base
 * size; a short glyph keeps the full 84 px. Measuring rather than hard-coding a
 * size means a longer axis name cannot silently clip at the canvas edge. Same
 * clamp-shrink approach as the CML labels (`forgeService.js:4230-4237`).
 */
const LABEL_FONT_PX = 84;
const LABEL_MIN_FONT_PX = 40;
/** Side padding inside the canvas, so the stroke outline is never clipped. */
const LABEL_PAD_PX = 14;

/**
 * CAD convention — X red, Y green, Z blue — carrying the report's own row
 * labels. See the header on why the survey names are here rather than letters.
 */
const AXES = Object.freeze([
  { label: 'X · Eastings', dir: Object.freeze([1, 0, 0]), color: '#EE1111' },
  { label: 'Y · Northings', dir: Object.freeze([0, 1, 0]), color: '#11AA11' },
  { label: 'Z · Elevation', dir: Object.freeze([0, 0, 1]), color: '#1166EE' },
]);

/**
 * North indicator. Amber deliberately sits OUTSIDE the RGB axis palette: north
 * is a different kind of statement from an axis, and red would read as a second
 * X. Floated above the box on its own so it cannot be mistaken for a fourth
 * axis emanating from the same corner, and shortened relative to the triad so
 * it stays visibly secondary.
 */
const NORTH_COLOR = '#FFB300';
const NORTH_LENGTH_RATIO = 0.8;
/** Clearance above `box.max.z`, as a fraction of box height. */
const NORTH_HEIGHT_RATIO = 0.15;

/**
 * The one triad currently on screen, or null. Module-level because the Forge
 * viewer is itself a cross-route singleton (cached in Vuex via
 * SET_PAGE_VIEW_3D) and only one verification workspace mounts at a time.
 *
 * Carries `cameraHandler` too: the billboard listener's lifetime is exactly the
 * triad's, so parking it here is what stops `removeAxisTriad` forgetting it.
 */
let activeTriad = null;

/**
 * Identity of a drawn triad: the anchor box rounded to millimetres, plus the
 * resolved arrow length. Everything else — north anchor, north length, every
 * label placement — is a pure function of those two, so this covers the whole
 * assembly. Lets a repeat `drawAxisTriad` for the same box return without
 * tearing down and rebuilding the triad and its listener.
 */
function triadKey(box, length, sizeScale, mmPerViewerUnit) {
  const mm = (v) => Math.round(v * mmPerViewerUnit);
  return [
    mm(box.min.x), mm(box.min.y), mm(box.min.z),
    mm(box.max.x), mm(box.max.y), mm(box.max.z),
    mm(length),
    // `length` already folds in the size multiplier, so this is belt-and-braces
    // — but it keeps the key honest if the two ever stop being related.
    Math.round(sizeScale * 100),
  ].join(',');
}

/** Guards the maths only — the UI's 0.2 floor is a store-side display policy. */
function clampOpacity(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.min(1, Math.max(0, numeric)) : 1;
}

/**
 * Push `opacity` onto every material the triad owns, in place.
 *
 * The transparency flag is the subtle part. Labels are ALWAYS transparent —
 * their canvas alpha depends on it. The solid parts (arrow lines, cone heads,
 * shafts) only join the transparent pass once the user actually dials opacity
 * below 1: at full opacity, leaving them opaque preserves the draw order that
 * puts every label over every arrow. Flipping them transparent unconditionally
 * would move them into the depth-sorted transparent pass, where an arrow can
 * sort in front of a label at some camera angles — a visible regression on the
 * rendering the user just signed off.
 *
 * `needsUpdate` therefore fires at most twice per drag (crossing 1.0 in each
 * direction), never per pixel; every other change is a bare value write.
 */
function applyTriadOpacity(entry, opacity) {
  const translucent = opacity < 1;
  const setMaterial = (material, alwaysTransparent) => {
    if (!material) return;
    material.opacity = opacity;
    const wantsTransparent = alwaysTransparent || translucent;
    if (material.transparent !== wantsTransparent) {
      material.transparent = wantsTransparent;
      material.needsUpdate = true;
    }
  };

  entry.objects.forEach((obj) => {
    setMaterial(obj.material, obj.name === LABEL_NAME);
    setMaterial(obj.line?.material, false);
    setMaterial(obj.cone?.material, false);
  });
}

/**
 * Text as a camera-facing billboard: a textured plane, NOT a sprite. See the
 * header — sprites render nothing in this bundle's overlay scenes.
 *
 * @param {string} text   Rendered as-is; shrink-to-fit guarantees it lands
 *   inside the canvas whatever its length.
 * @param {string} color  CSS colour for the glyph fill.
 * @param {number} height Plane height in VIEWER units — it is a fraction of the
 *   arrow length, so it inherits whatever space the box was in.
 * @param {object} [opts] `canvasWidth` (default square) and `align` — 'left'
 *   anchors a long name at the canvas edge so it reads outward from the arrow
 *   tip; 'center' suits a single glyph.
 * @returns {{ mesh: object, texture: object, width: number }} texture is
 *   returned separately because `removeFromScene` disposes materials but never
 *   their maps; width so the caller can offset without redoing the aspect.
 */
function createLabelMesh(text, color, height, opts = {}) {
  const THREE = window.THREE;
  const { canvasWidth = GLYPH_CANVAS_W_PX, align = 'center' } = opts;

  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = LABEL_CANVAS_H_PX;

  // Sizing the canvas resets the 2D context, so every ctx call belongs below it.
  const ctx = canvas.getContext('2d');
  ctx.font = `bold ${LABEL_FONT_PX}px Arial`;
  const available = canvas.width - LABEL_PAD_PX * 2;
  const measured = ctx.measureText(text).width;
  const fontPx = measured > available
    ? Math.max(LABEL_MIN_FONT_PX, Math.floor(LABEL_FONT_PX * (available / measured)))
    : LABEL_FONT_PX;

  ctx.font = `bold ${fontPx}px Arial`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  // Dark outline beneath the axis colour, same contrast guarantee the CML
  // labels use: a green Y over a pale point cloud is otherwise unreadable.
  ctx.lineJoin = 'round';
  ctx.lineWidth = Math.max(6, Math.floor(fontPx / 8));
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
  const x = align === 'left' ? LABEL_PAD_PX : canvas.width / 2;
  ctx.strokeText(text, x, canvas.height / 2);
  ctx.fillStyle = color;
  ctx.fillText(text, x, canvas.height / 2);

  const texture = new THREE.Texture(canvas);
  texture.needsUpdate = true;

  // Plane width tracks the canvas aspect, so the texture is never distorted.
  const width = height * (canvas.width / canvas.height);
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
  mesh.name = LABEL_NAME;

  return { mesh, texture, width };
}

/**
 * An `ArrowHelper` carrying a head this bundle actually draws.
 *
 * The stock head is invisible here, so the cone geometry is replaced outright —
 * the `threejsUtils.js:291` pattern. Two r71 details make that fiddlier than it
 * looks: `setLength` SCALES the cone mesh by (headWidth, headLength, headWidth),
 * which would multiply our already-sized geometry, and the cone's
 * `matrixAutoUpdate` is false so its matrix must be refreshed by hand.
 */
function createArrow(direction, origin, length, color) {
  const THREE = window.THREE;
  const headLength = length * HEAD_LENGTH_RATIO;
  const headRadius = (length * HEAD_WIDTH_RATIO) / 2;

  const arrow = new THREE.ArrowHelper(
    direction,
    origin,
    length,
    parseInt(color.slice(1), 16),
    headLength,
    headRadius * 2,
  );

  const cone = new THREE.CylinderGeometry(0, headRadius, headLength, 16);
  // r71's stock cone has its TIP at local y=0 and its base at y=-1, which is
  // what makes `cone.position.y = length` land the point exactly on the arrow
  // tip. A fresh CylinderGeometry is centred on its own origin, so shift it down
  // by half its height to adopt that same convention.
  cone.applyMatrix(new THREE.Matrix4().makeTranslation(0, -headLength / 2, 0));
  arrow.cone.geometry = cone;
  // Our geometry is already true-size; drop the scale `setLength` applied.
  arrow.cone.scale.set(1, 1, 1);
  arrow.cone.position.y = length;
  arrow.cone.updateMatrix();

  arrow.name = 'axis-triad-arrow';
  arrow.line.material.depthTest = false;
  arrow.line.material.depthWrite = false;
  arrow.cone.material.depthTest = false;
  arrow.cone.material.depthWrite = false;
  return arrow;
}

/**
 * Keep every label facing the camera by copying the camera's quaternion onto it
 * — the CML billboard approach (`forgeService.js:4455-4456`).
 *
 * Attached here, detached in `removeAxisTriad`. Those are the only two places
 * that touch it, and `drawAxisTriad` always removes before it redraws, so a
 * second listener can never accumulate.
 *
 * @returns {Function|null} the handler, to hand back to `removeEventListener`.
 */
function attachBillboardHandler(viewer, labels) {
  const event = window.Autodesk?.Viewing?.CAMERA_CHANGE_EVENT;
  if (!event || typeof viewer?.addEventListener !== 'function') {
    debugWarn('[axisTriad] billboard listener unavailable — labels will not track the camera');
    return null;
  }

  const handler = () => {
    const camera = viewer.getCamera?.();
    if (!camera) return;
    labels.forEach((mesh) => mesh.quaternion.copy(camera.quaternion));
    // The quaternion written on the LAST camera-change event would otherwise
    // wait for some unrelated repaint; CML invalidates here for the same reason.
    viewer.impl?.invalidate?.(true);
  };

  viewer.addEventListener(event, handler);
  // Orient once immediately — without this the labels sit edge-on, and so
  // effectively invisible, until the user first moves the camera.
  handler();
  return handler;
}

/**
 * Draw the triad at `box.min` plus the north indicator above the box, replacing
 * anything already on screen.
 *
 * Idempotent: called again with the same box it is a no-op, so a caller may
 * fire it from a watcher without stacking overlays.
 *
 * @param {object} viewer Autodesk viewer instance.
 * @param {object} box    THREE.Box3 — the ORIGINAL support crop box, VIEWER units.
 * @param {object} [opts] `sizeScale` multiplies every dimension; `opacity` is
 *   applied to the finished assembly. Opacity is passed here only so a fresh
 *   draw honours the current setting — CHANGING it afterwards must go through
 *   `setAxisTriadOpacity`, which needs no rebuild.
 * @param {object|null} [opts.unitTransform] REQUIRED in practice — the frozen
 *   transform from the store (`GET_UNIT_TRANSFORM`). This module never resolves
 *   units itself: a live viewer read here could pick a different model than the
 *   one the rest of the feature agreed on. Without it, nothing is drawn.
 * @returns {boolean} true when a triad is on screen for `box`.
 */
export function drawAxisTriad(viewer, box, opts = {}) {
  const THREE = window.THREE;
  const sizeScale = Number.isFinite(opts.sizeScale) && opts.sizeScale > 0 ? opts.sizeScale : 1;
  const opacity = clampOpacity(opts.opacity);
  if (!viewer || !box || !THREE) {
    debugWarn('[axisTriad] draw skipped — missing viewer, box or THREE', {
      hasViewer: !!viewer, hasBox: !!box, hasThree: !!THREE,
    });
    return false;
  }

  // Every dimension below is either a metre constant needing conversion or a
  // millimetre readout produced by it, and `triadKey` rounds the box THROUGH it
  // — with no scale the key collapses to all-zeros, so two different boxes
  // would compare equal and the idempotence check would suppress a real redraw.
  // Refusing is recoverable: the caller re-runs when the transform commits.
  const mmPerViewerUnit = opts.unitTransform?.mmPerViewerUnit ?? null;
  const viewerUnitsPerMetre = opts.unitTransform?.siteScale > 0
    ? 1 / opts.unitTransform.siteScale
    : null;
  if (!mmPerViewerUnit || !viewerUnitsPerMetre) {
    debugWarn('[axisTriad] draw skipped — no unit transform yet', {
      unitTransform: opts.unitTransform ?? null,
    });
    return false;
  }

  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  if (!Number.isFinite(maxDim) || maxDim <= 0) {
    debugWarn('[axisTriad] draw skipped — degenerate box', { size });
    return false;
  }

  // The clamp bounds the DERIVED size, then the user's multiplier is applied on
  // top. Clamping after the multiply would make "2×" mean "2× unless you were
  // already at the ceiling", which is exactly the surprise a size control
  // exists to avoid. Every other dimension — heads, shafts, labels, north — is
  // a ratio of this, so they all scale with it for free.
  // Both bounds are metres; `maxDim` is viewer units. Convert the bounds, never
  // the box — see the constants' docblock.
  const length = Math.min(
    AXIS_LENGTH_MAX_M * viewerUnitsPerMetre,
    Math.max(AXIS_LENGTH_MIN_M * viewerUnitsPerMetre, maxDim * AXIS_LENGTH_RATIO),
  ) * sizeScale;

  const key = triadKey(box, length, sizeScale, mmPerViewerUnit);
  if (activeTriad && activeTriad.viewer === viewer && activeTriad.key === key) {
    return true;
  }
  removeAxisTriad(viewer);

  const objects = [];
  const textures = [];
  const labels = [];

  /** One arrow — shaft, head and label — parked in the shared teardown lists. */
  const addLabelledArrow = (origin, direction, arrowLength, color, label, labelOpts) => {
    const hex = parseInt(color.slice(1), 16);

    const arrow = createArrow(direction, origin, arrowLength, color);
    addToScene(arrow, viewer, SCENE_NAME);
    objects.push(arrow);

    // Solid shaft over the hairline — see the header. `drawCylinder` adds itself
    // to the scene, already builds its material with both depth flags off, and
    // owns its geometry, so `removeFromScene` can release it unaided. It stops
    // at the cone's base so the shaft can never poke through the point.
    const shaftEnd = origin.clone().add(
      direction.clone().multiplyScalar(arrowLength * (1 - HEAD_LENGTH_RATIO)),
    );
    const shaftRadius = arrowLength * SHAFT_RADIUS_RATIO;
    objects.push(
      drawCylinder(origin, shaftEnd, shaftRadius, shaftRadius, hex, viewer, SCENE_NAME),
    );

    const height = arrowLength * LABEL_SCALE_RATIO;
    const { mesh, texture, width } = createLabelMesh(label, color, height, labelOpts);
    // A label's `width` is its SCREEN-horizontal extent once billboarded, so
    // half-width is the correct push-out only for an axis that projects roughly
    // horizontally. World-up projects to screen-VERTICAL in every practical
    // view, where the half-extent along the axis is the label's HEIGHT instead —
    // pushing by half-width there would float the Z label hundreds of
    // millimetres above its own tip, into the north indicator's screen region.
    const halfExtent = Math.abs(direction.z) > 0.9 ? height / 2 : width / 2;
    mesh.position.copy(
      origin.clone().add(
        direction.clone().multiplyScalar(
          arrowLength + height * LABEL_TIP_GAP_RATIO + halfExtent,
        ),
      ),
    );
    addToScene(mesh, viewer, SCENE_NAME);
    objects.push(mesh);
    textures.push(texture);
    labels.push(mesh);
  };

  const origin = box.min.clone();
  AXES.forEach(({ label, dir, color }) => {
    addLabelledArrow(
      origin,
      new THREE.Vector3(dir[0], dir[1], dir[2]),
      length,
      color,
      label,
      { canvasWidth: AXIS_LABEL_CANVAS_W_PX, align: 'left' },
    );
  });

  // North floats over the middle of the box rather than sharing the triad's
  // corner: from that corner it would read as a fourth axis.
  const center = box.getCenter(new THREE.Vector3());
  const northOrigin = new THREE.Vector3(
    center.x,
    center.y,
    box.max.z + size.z * NORTH_HEIGHT_RATIO,
  );
  addLabelledArrow(
    northOrigin,
    new THREE.Vector3(0, 1, 0),
    length * NORTH_LENGTH_RATIO,
    NORTH_COLOR,
    'N',
  );

  const cameraHandler = attachBillboardHandler(viewer, labels);
  activeTriad = { viewer, key, objects, textures, cameraHandler };
  applyTriadOpacity(activeTriad, opacity);
  // Overlay adds only mark the scene dirty on the next redraw; without this the
  // triad does not appear until the camera moves. Same reason
  // Cropping3DExtension invalidates after every chrome change.
  viewer.impl?.invalidate?.(true, true, true);

  debugLog('[axisTriad] drawn', {
    mmPerViewerUnit,
    arrowLengthMm: Math.round(length * mmPerViewerUnit),
    headLengthMm: Math.round(length * HEAD_LENGTH_RATIO * mmPerViewerUnit),
    shaftRadiusMm: Math.round(length * SHAFT_RADIUS_RATIO * mmPerViewerUnit),
    northLengthMm: Math.round(length * NORTH_LENGTH_RATIO * mmPerViewerUnit),
    boxMaxDimMm: Math.round(maxDim * mmPerViewerUnit),
    origin: { x: origin.x, y: origin.y, z: origin.z },
    northOrigin: { x: northOrigin.x, y: northOrigin.y, z: northOrigin.z },
    objects: objects.length,
    billboarded: !!cameraHandler,
    sizeScale,
    opacity,
  });
  return true;
}

/**
 * Re-tint the live triad without rebuilding it.
 *
 * This is the whole reason opacity and size are wired differently: opacity is a
 * material property, so it can ride an `@input` slider at one write per pixel of
 * drag, while size changes geometry and has to rebuild — which is why its slider
 * commits on release instead.
 *
 * @param {object} [viewer] Autodesk viewer instance.
 * @param {number} value    0-1. The 0.2 floor the UI enforces is a display
 *   policy that lives in the store, not here; this only guards the maths.
 * @returns {boolean} false when no triad is drawn, so a caller can tell
 *   "nothing to do" from "applied".
 */
export function setAxisTriadOpacity(viewer, value) {
  const entry = activeTriad;
  if (!entry) return false;

  applyTriadOpacity(entry, clampOpacity(value));
  (entry.viewer || viewer)?.impl?.invalidate?.(true, true, true);
  return true;
}

/**
 * Take the triad down, detach its camera listener, and release everything it
 * owns.
 *
 * Safe to call with no triad drawn, and safe to call with a null `viewer` —
 * teardown can run after the route has dropped its viewer reference, and the
 * listener and GPU resources still need releasing.
 *
 * @param {object} [viewer] Autodesk viewer instance.
 * @returns {boolean} true when a triad was removed.
 */
export function removeAxisTriad(viewer) {
  const entry = activeTriad;
  if (!entry) return false;
  activeTriad = null;

  // The viewer we DREW into wins over the one we were handed. They differ only
  // if the viewer was replaced while a triad was live (facility switch), and in
  // that case detaching against the new viewer would silently find no scene —
  // leaving our objects on the old one with disposed materials, and leaving the
  // billboard listener attached to it forever.
  const target = entry.viewer || viewer;

  const event = window.Autodesk?.Viewing?.CAMERA_CHANGE_EVENT;
  if (entry.cameraHandler && event && typeof target?.removeEventListener === 'function') {
    target.removeEventListener(event, entry.cameraHandler);
  }

  // `removeFromScene` detaches and disposes `.geometry` / `.material`, which
  // covers the label planes and the shaft cylinders. An ArrowHelper is a bare
  // Object3D — no `.geometry`, no `.material` — so its parts are released below.
  removeFromScene(entry.objects, target, SCENE_NAME);
  entry.objects.forEach((obj) => {
    obj.line?.material?.dispose?.();
    obj.cone?.material?.dispose?.();
    // The cone geometry is OURS — `createArrow` replaced r71's stock one with a
    // sized head. The LINE geometry is still the shared singleton every
    // ArrowHelper in the app draws from; disposing that would blank the crop
    // box's control arrows. See the file header.
    obj.cone?.geometry?.dispose?.();
  });
  entry.textures.forEach((texture) => texture?.dispose?.());

  target?.impl?.invalidate?.(true, true, true);
  debugLog('[axisTriad] removed', {
    objects: entry.objects.length,
    listenerDetached: !!entry.cameraHandler,
  });
  return true;
}
